<script lang="ts">
    import { trans } from '$assets/shop-translator.js';
    import { TOUR_TYPE_GIR, TYPEBOOKING_EBOOKING } from '$saga/constants/tour';
    import PaxAddRoomButton from '$saga/views/shared/RoomPax/PaxAddRoomButton.svelte';
    import PaxHiddenInputs from '$saga/views/shared/RoomPax/PaxHiddenInputs.svelte';
    import PaxRemainingStockNotice from '$saga/views/shared/RoomPax/PaxRemainingStockNotice.svelte';
    import PaxRoomCard from '$saga/views/shared/RoomPax/PaxRoomCard.svelte';
    import PaxStepIntro from '$saga/views/shared/RoomPax/PaxStepIntro.svelte';
    import PaxSummary from '$saga/views/shared/RoomPax/PaxSummary.svelte';
    import type { AgeRules } from '$saga/views/Tour/types';

    export let tourType: number;
    export let bookingType: string;
    export let isTravelAloneAuthorized: boolean;
    export let minAgeAllowed: number;
    export let maxAgeAllowed: number;
    export let childrenAgesConfig: { [key: string]: string };
    export let datesInfos;

    const isEbookingType = bookingType === TYPEBOOKING_EBOOKING;
    const isGir = tourType === TOUR_TYPE_GIR;
    const isChildrenAllowed =
        isEbookingType &&
        (minAgeAllowed < 18 || (maxAgeAllowed && maxAgeAllowed < 18));
    const ageRules: AgeRules = {
        max: {
            isUnder18Allowed: maxAgeAllowed != null && maxAgeAllowed < 18,
        },
        min: {
            isUnder18Allowed: minAgeAllowed != null && minAgeAllowed < 18,
        },
    };
    type Room = {
        adults: number;
        children: number;
        childrenAges: string[];
    };
    type Occupancies = Room[];

    let occupancies: Occupancies = (() => {
        if (datesInfos.occupancies && datesInfos.occupancies.length > 0)
            return datesInfos.occupancies;
        else if (datesInfos.totalRooms > 0) {
            return Array.from({ length: datesInfos.totalRooms }).map(() => ({
                adults: 1,
                children: 0,
                childrenAges: [],
            }));
        } else {
            return [
                {
                    adults: 1,
                    children: 0,
                    childrenAges: [],
                },
            ];
        }
    })();

    const addRoom = (): void => {
        occupancies = [
            ...occupancies,
            {
                adults: 1,
                children: 0,
                childrenAges: [],
            },
        ];
    };

    const removeRoom = (index: number): void => {
        occupancies = occupancies.filter((_, roomIndex) => roomIndex !== index);
    };

    $: roomCount = occupancies.length;
    $: totalAdults = occupancies.reduce((sum, room) => sum + room.adults, 0);
    $: totalChildren = occupancies.reduce(
        (sum, room) => sum + room.children,
        0,
    );
    $: flatChildrenAges = occupancies.map((room) => room.childrenAges).flat();
    $: adultAges = Array.from(
        { length: totalAdults },
        (_, index) => datesInfos.adultAges?.[index] ?? 18,
    );
    $: totalAdultsError = !isTravelAloneAuthorized && totalAdults === 1;
    // Gir specific
    $: maxAdultsRuleValue =
        isEbookingType && isGir && datesInfos?.remainingStock
            ? datesInfos.remainingStock - totalChildren
            : null;
    $: maxChildrenRuleValue =
        isGir && isChildrenAllowed && datesInfos.remainingStock
            ? datesInfos.remainingStock - totalAdults
            : null;
</script>

<div class="flex h-full flex-col gap-xl pt-2xl">
    <PaxStepIntro />

    <PaxSummary
        {totalAdults}
        {totalChildren}
        showAdultsError={totalAdultsError}
    />

    {#each occupancies as room, index (index)}
        <PaxRoomCard
            {index}
            adultCount={room.adults}
            childCount={room.children}
            showChildren={Boolean(isChildrenAllowed)}
            canIncrementAdults={totalAdults !== maxAdultsRuleValue}
            canIncrementChildren={maxChildrenRuleValue !== totalChildren}
            childrenMax={maxChildrenRuleValue}
            {minAgeAllowed}
            {maxAgeAllowed}
            {ageRules}
            onAdultsIncrement={() => {
                if (
                    maxAdultsRuleValue == null ||
                    maxAdultsRuleValue > totalAdults
                ) {
                    room.adults = room.adults + 1;
                }
            }}
            onAdultsDecrement={() => {
                if (room.adults > 1) {
                    room.adults = room.adults - 1;
                }
            }}
            onChildrenIncrement={() => {
                if (
                    maxChildrenRuleValue == null ||
                    maxChildrenRuleValue > totalChildren
                ) {
                    room.children = room.children + 1;
                    room.childrenAges = [...room.childrenAges, ''];
                }
            }}
            onChildrenDecrement={() => {
                if (room.children > 0) {
                    room.children = room.children - 1;
                    room.childrenAges = room.childrenAges.slice(0, -1);
                }
            }}
            showDelete={roomCount > 1}
            onDelete={() => removeRoom(index)}
        >
            {#if isChildrenAllowed}
                <div class="text-xs text-content-quiet">
                    {#if totalChildren > 0}
                        {trans(
                            'tour.aside_panel.pax_popover.children_details.text',
                        )}
                    {/if}
                </div>

                <!--  eslint-disable-next-line @typescript-eslint/no-unused-vars }-->
                {#each room.childrenAges as childrenAge, childIndex (childIndex)}
                    <div class="flex items-center gap-2xl">
                        <div class="whitespace-nowrap text-sm">
                            {trans('tour.aside_panel.pax_popover.child.label')}
                            {childIndex + 1}
                        </div>
                        <select
                            class="min-h-l w-full"
                            required
                            bind:value={childrenAge}
                        >
                            <option class="pax-selector__placeholder" value="">
                                {trans(
                                    'tour.aside_panel.pax_popover.child.label',
                                )}
                                {childIndex + 1}
                            </option>
                            {#each Object.keys(childrenAgesConfig) as childrenAgeKey (childrenAgeKey)}
                                <option value={childrenAgeKey}>
                                    {childrenAgesConfig[childrenAgeKey]}
                                </option>
                            {/each}
                        </select>
                    </div>
                {/each}
            {/if}
        </PaxRoomCard>
    {/each}

    <PaxAddRoomButton
        {roomCount}
        canIncrement={totalAdults + totalChildren !==
            datesInfos?.remainingStock}
        onAdd={addRoom}
    />

    <PaxRemainingStockNotice remainingStock={datesInfos?.remainingStock} />
</div>

<PaxHiddenInputs {occupancies} {totalAdults} {totalChildren} />

{#each adultAges as age, idx (idx)}
    <input tabindex="-1" class="hidden" name="adultAge[]" value={age} />
{/each}

{#if totalChildren > 0}
    {#each flatChildrenAges as age, idx (idx)}
        <input tabindex="-1" class="hidden" name="childrenAge[]" value={age} />
    {/each}
{/if}
